import React, { useState, useEffect } from "react";
import { TagService } from "../../../services/api/admin.service";
import { toast } from "react-toastify";

const BlogTagsManager = ({
  onTagSelect,
  selectedTag = null,
  className = "",
}) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTag, setActiveTag] = useState(selectedTag);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTag, setCurrentTag] = useState({ name: "", description: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("usage");
  const [sortOrder, setSortOrder] = useState("desc");

  // Fetch tags when component mounts or when search/sort parameters change
  useEffect(() => {
    fetchTags();
  }, [searchTerm, sortBy, sortOrder]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const fetchedTags = await TagService.getAllTags({
        search: searchTerm,
        sortBy,
        sortOrder,
        limit: 50,
      });
      setTags(fetchedTags);
      setError(null);
    } catch (err) {
      setError("Failed to load tags. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // const fetchTags = async () => {
  //   try {
  //     setLoading(true);
  //     const fetchedTags = await TagService.getAllTags({
  //       search: searchTerm,
  //       sortBy,
  //       sortOrder,
  //       limit: 50,
  //     });

  //     setTags(fetchedTags);
  //     setError(null);
  //   } catch (err) {
  //     setError("Failed to load tags. Please try again later.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
    setActiveTag(selectedTag);
  }, [selectedTag]);

  const handleTagClick = (tagId) => {
    const newActiveTag = activeTag === tagId ? null : tagId;
    setActiveTag(newActiveTag);

    if (onTagSelect) {
      onTagSelect(newActiveTag);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTag((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!currentTag.name.trim()) return;

    try {
      setLoading(true);
      const createdTag = await TagService.createTag({
        ...currentTag,
        slug: currentTag.name.trim().toLowerCase().replace(/\s+/g, "-"), // Add this line
      });
      await fetchTags();
      setCurrentTag({ name: "", description: "" });
      setIsCreating(false);
      toast.success("Tag created successfully!");
    } catch (err) {
      setError("Failed to create tag. Please try again.");
      toast.error(err.message || "Failed to create tag");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTag = async (e) => {
    e.preventDefault();
    if (!currentTag.name.trim() || !currentTag._id) return;

    try {
      setLoading(true);
      await TagService.updateTag(currentTag._id, currentTag);
      await fetchTags();
      setCurrentTag({ name: "", description: "" });
      setIsEditing(false);
    } catch (err) {
      setError("Failed to update tag. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!tagId || !window.confirm("Are you sure you want to delete this tag?"))
      return;

    try {
      setLoading(true);
      const result = await TagService.deleteTag(tagId);
      await fetchTags();
      if (activeTag === tagId) {
        setActiveTag(null);
        if (onTagSelect) onTagSelect(null);
      }
      toast.success("Tag deleted successfully!");
    } catch (err) {
      // Check for the specific error message about tag being in use
      if (
        err?.message?.toLowerCase().includes("cannot delete tag that is in use")
      ) {
        setError("Cannot delete a tag that is currently in use by blog posts.");
        toast.error("Cannot delete a tag that is currently in use");
      } else {
        setError("Failed to delete tag. Please try again.");
        toast.error(err.message || "Failed to delete tag");
      }
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (tag) => {
    setCurrentTag({ ...tag });
    setIsEditing(true);
    setIsCreating(false);
  };

  const cancelForm = () => {
    setIsCreating(false);
    setIsEditing(false);
    setCurrentTag({ name: "", description: "" });
  };

  const renderSkeletonLoader = () => (
    <div className="space-y-4">
      <div className="h-8 bg-gray-200 rounded animate-pulse w-full max-w-xs"></div>
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-10 bg-gray-200 rounded-md animate-pulse w-28"
          ></div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`bg-white rounded-lg  ${className}`}>
      {/* Header */}
      <div className="border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Tags Manager</h2>
        {!isCreating && !isEditing && (
          <button
            onClick={() => {
              setIsCreating(true);
              setIsEditing(false);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
          >
            Create New Tag
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {error}
          <button
            className="float-right text-red-600 hover:text-red-800"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Create/Edit Form */}
      {(isCreating || isEditing) && (
        <div className="p-6 border-b">
          <h3 className="text-lg font-medium mb-4">
            {isCreating ? "Create New Tag" : "Edit Tag"}
          </h3>
          <form onSubmit={isCreating ? handleCreateTag : handleUpdateTag}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Tag Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={currentTag.name}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={currentTag.description || ""}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows="3"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelForm}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isCreating ? "Create" : "Update"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Search and Sort Section */}
      <div className="p-6 border-b">
        <div className="flex flex-wrap gap-4">
          <div className="w-full sm:w-auto flex-1">
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Search Tags
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Search by name..."
            />
          </div>
          <div className="w-full sm:w-auto">
            <label
              htmlFor="sortBy"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Sort By
            </label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="usage">Usage</option>
              <option value="name">Name</option>
              <option value="createdAt">Created Date</option>
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label
              htmlFor="sortOrder"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Order
            </label>
            <select
              id="sortOrder"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tags List */}
      <div className="p-6">
        <h3 className="text-lg font-medium mb-4">All Tags</h3>

        {loading && renderSkeletonLoader()}

        {!loading && tags.length === 0 && (
          <p className="text-gray-500 py-4">
            No tags found. Create a new tag to get started.
          </p>
        )}

        {!loading && tags.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tags.map((tag) => (
                  <tr key={tag._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">
                        {tag.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-500">{tag.usage || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-500 line-clamp-2">
                        {tag.description || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleTagClick(tag._id)}
                          className={`px-2 py-1 rounded text-xs font-medium ${activeTag === tag._id
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                          {activeTag === tag._id ? "Selected" : "Select"}
                        </button>
                        <button
                          onClick={() => startEditing(tag)}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium hover:bg-blue-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag._id)}
                          className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Popular Tags Section */}
      <div className="p-6 bg-gray-50 rounded-b-lg">
        <h3 className="text-lg font-medium mb-4">Popular Tags</h3>
        <div className="flex flex-wrap gap-2">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-8 bg-gray-200 rounded-md animate-pulse w-20"
              ></div>
            ))
          ) : tags.length > 0 ? (
            tags
              // .sort((a, b) => (b.usage || 0) - (a.usage || 0))
              .slice(0, 10)
              .map((tag) => (
                <button
                  key={tag._id}
                  onClick={() => handleTagClick(tag._id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTag === tag._id
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {tag.name}
                  <span className="ml-1 text-xs opacity-80">
                    ({tag.usage || 0})
                  </span>
                </button>
              ))
          ) : (
            <p className="text-gray-500 text-sm">No tags available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogTagsManager;
